import { computed, ref, onMounted } from "vue";
import { Capacitor } from "@capacitor/core";

type MobilePlatform = "ios" | "android" | null;

const DISMISS_KEY = "appPromotionBannerDismissedAt";
const COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;

const IOS_STORE_URL =
    import.meta.env.VUE_APP_IOS_APP_STORE_URL ||
    "https://apps.apple.com/app/sportspoule-football-scores/id6472849536";
const ANDROID_STORE_URL =
    import.meta.env.VUE_APP_ANDROID_PLAY_STORE_URL ||
    "https://play.google.com/store/apps/details?id=app.sportspoule.com";

function detectMobilePlatform(): MobilePlatform {
    if (typeof navigator === "undefined") {
        return null;
    }
    const ua = navigator.userAgent || "";
    if (/iPhone|iPad|iPod/i.test(ua)) {
        return "ios";
    }
    if (/Android/i.test(ua)) {
        return "android";
    }
    return null;
}

export function useAppPromotion() {
    const dismissed = ref(false);
    const platform = ref<MobilePlatform>(null);

    onMounted(() => {
        if (Capacitor.isNativePlatform()) {
            return;
        }
        platform.value = detectMobilePlatform();
        try {
            const raw = localStorage.getItem(DISMISS_KEY);
            if (raw) {
                const at = Number(raw);
                if (Number.isFinite(at) && Date.now() - at < COOLDOWN_MS) {
                    dismissed.value = true;
                }
            }
        } catch {
            // localStorage unavailable, ignore
        }
    });

    const storeUrl = computed(() => {
        if (platform.value === "ios") {
            return IOS_STORE_URL;
        }
        if (platform.value === "android") {
            return ANDROID_STORE_URL;
        }
        return null;
    });

    const visible = computed(
        () =>
            !Capacitor.isNativePlatform() &&
            !dismissed.value &&
            storeUrl.value !== null,
    );

    function dismiss(): void {
        dismissed.value = true;
        try {
            localStorage.setItem(DISMISS_KEY, String(Date.now()));
        } catch {
            // ignore
        }
    }

    return {
        visible,
        platform,
        storeUrl,
        dismiss,
    };
}
